import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { Account, PolicyControl, PolicyControlPostRequest } from '@mitel/cloudlink-sdk/admin';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { UserRoles } from 'src/app/core/constants/constants';
import { AccountsService } from 'src/app/core/services/accounts.service';
import { AdminService } from 'src/app/core/services/admin.service';
import { AppUserClaimsService } from 'src/app/core/services/app-user-claims.service';
import { UsersService } from 'src/app/core/services/users.service';
import { Util } from 'src/app/core/utils/Util';
import { Utils } from '@mitel/cloudlink-sdk';
import { ProgressIndicatorPopupComponent } from 'src/app/shared/components/progress-indicator-popup/progress-indicator-popup.component';
import _, { update } from 'lodash';
import { AccountData } from '../components/manager-accounts-list/manager-accounts-list.component';

export interface ManagedAccount {
  accountId: string;
  managers: { userId: string, name: string }[];
  policyControls?: any[];
}

export interface AccountManager extends User {
  policyControls: PolicyControl[];
  managedAccounts: string[];
}

export enum policyControlAction {
  ASSIGN = 'assign',
  UNASSIGN = 'unassign',
  RESTRICT = 'restrict',
  UNRESTRICT = 'unrestrict',
};

export class ProgressIndicatorData {
  total: number;
  done: number;
  errorMessage: string;
  title: string;
  actionString: string;
  successMessage: string;
  updateSubject: any;
  constructor() {}
}

@Injectable({
  providedIn: 'root'
})
export class AccountManagementService {
  selectedUsers: User[] = [];

  constructor(
    private modalService: NgbModal,
    private translateService: TranslateService,
    private adminService: AdminService,
    private accountsService: AccountsService,
    private userClaimsService: AppUserClaimsService,
  ) { }

  /**
   * Do not show the "Account Managers" Feature for Proximus Accounts
   * https://mitel.atlassian.net/browse/CLSD-4927
   * @returns true unless Proximus account.
   */
  async isAccountManagementFeatureAllowed(): Promise<boolean> {
    return this.accountsService.isProximusAccount()
      .then(isParentProximus => !isParentProximus)
      .catch(err => false);
  }

  /**
   * Determines whether to show the account managers menu item based on if we can show the feature
   * and whether the logged in user has policy controls or not.
   * @returns A promise that resolves to a boolean value indicating whether to show the menu item.
   */
  async showAccountManagersMenuItem(): Promise<boolean> {
    return this.isAccountManagementFeatureAllowed()
    .then((isFeatureAllowed) => {
      if (isFeatureAllowed) {
        return this.getPolicyControlsForUser(this.userClaimsService.getLoggedInUser()?.userId)
          .then(policyControls => !(policyControls && policyControls.length > 0));
      } else {
        return false;
      }
    })
    .catch(err => false);
  }

  isPrimeAccountId(accountId: string) {
    return accountId == this.accountsService.primeAccount;
  }

  /**
   * Retrieves all the policy controls. (default is to retrieve 50 policy controls)
   * 
   * @param params - Optional parameters for the request.
   * @returns A promise that resolves to an array of policy controls.
   */
  async getPolicyControls(params?: any): Promise<PolicyControl[]> {
    return this.adminService.getPolicyControls(params).then((policyControlCollection) => {
      let policyControls = Utils.getItemsFromCollection(policyControlCollection);
      const next = Utils.getOdataNext(policyControlCollection);
      if (next) {
        // other params not supported for now
        let nextParams = {
          query: {
            $top: next.$Top,
            $skipToken: next.$SkipToken,
          }
        };
        return this.getPolicyControls(nextParams).then((nextPolicyControls) => {
          policyControls = policyControls.concat(nextPolicyControls);
          return policyControls;
        });
      }
      return policyControls
    });
  }

  /**
   * Retrieves the policy controls for a specific user.
   * @param userId The ID of the user.
   * @returns A promise that resolves to an array of PolicyControls.
   */
  getPolicyControlsForUser(userId: string): Promise<PolicyControl[]> {
		let params = {
			query: {
				$filter: `principalId eq '${userId}'`
			}
		};
		return this.getPolicyControls(params);
	}

  /**
   * Retrieves the users associated with the account.
   * Filters out users with the role of PARTNER_ADMIN and the same userId as the current user.
   * 
   * @returns A promise that resolves to an array of filtered users.
   */
  async getUsers() {
    return this.adminService.getUsersByAccountIdWithoutSpinner(this.accountsService.primeAccount).then((users) => {
      let claims = this.userClaimsService.getClaims() as any;
      return users.filter((user) => user.role == UserRoles.PARTNER_ADMIN && claims.userId != user.userId);
    });
  }

  /**
   * Retrieves a list of managed accounts.
   * 
   * @returns A promise that resolves to an array of ManagedAccount objects.
   */
  async getListOfManagedAccounts() {
    return this.getPolicyControls().then(async (res) => {
      let users = await this.adminService.getUsersByAccountIdWithoutSpinner(this.accountsService.primeAccount);
      return res.reduce((managedAccounts: ManagedAccount[], policyControl) => {
        // Don't show prime account policy control
        if (policyControl.targetAccountId == this.accountsService.primeAccount) {
          return managedAccounts;
        }
        
        let managedAccount = managedAccounts.find((managedAccount) => managedAccount.accountId == policyControl.targetAccountId);
        let user = users.find((user) => user.userId == policyControl.principalId);
        if (managedAccount) {
          managedAccount.managers.push({ userId: policyControl.principalId, name: user?.name });
          managedAccount.policyControls.push(policyControl);
        } else {
          managedAccounts.push({
            accountId: policyControl.targetAccountId,
            managers: [{ userId: policyControl.principalId, name: user?.name }],
            policyControls: [policyControl],
          });
        }
        return managedAccounts
      }, []);
    });
  }

  /**
   * Retrieves a list of managed accounts for the selected users.
   * If no users are selected, an empty array is returned.
   * 
   * @returns A promise that resolves to an array of ManagedAccount objects.
   */
  async getListOfManagedAccountsForSelectedUsers(): Promise<ManagedAccount[]> {
    if (!this.selectedUsers || this.selectedUsers.length == 0) {
      return Promise.resolve([]);
    }
    return this.getPolicyControls().then((policyControls: PolicyControl[]) => {
      return policyControls.reduce((managedAccounts: ManagedAccount[], policyControl: PolicyControl) => {
        // Don't show prime account policy control
        if (policyControl.targetAccountId == this.accountsService.primeAccount) {
          return managedAccounts;
        }
        let user = this.selectedUsers.find((user) => user.userId == policyControl.principalId);
        if (user) {
          let managedAccount = managedAccounts.find((managedAccount) => managedAccount.accountId == policyControl.targetAccountId);
          if (managedAccount) {
            managedAccount.managers.push({ userId: policyControl.principalId, name: user?.name });
            managedAccount.policyControls.push(policyControl);
          } else {
            managedAccounts.push({
              accountId: policyControl.targetAccountId,
              managers: [{ userId: policyControl.principalId, name: user?.name }],
              policyControls: [policyControl],
            });
          }
        }
        return managedAccounts
      }, []);
    });
  }

  /**
   * Sets the selected users based on the provided user IDs.
   * If the user IDs array is empty or null, the selected users will be cleared.
   * 
   * @param userIds - An array of user IDs.
   * @returns A promise that resolves with the selected users.
   */
  async setSelectedUsers(userIds: string[]) {
    if (userIds?.length > 0) {
      return this.adminService.getSpecificUsersFromAccount(this.accountsService.primeAccount, userIds, 'userId').then((users) => {
        this.selectedUsers = users;
        console.debug("Selected users: ", this.selectedUsers);
        return this.selectedUsers;
      }).catch((err) => {
        this.selectedUsers = [];
        console.error("There was an error fetching the specified users");
        return Promise.resolve(this.selectedUsers);

      });
    } else {
      this.selectedUsers = [];
      console.error("No users were selected");
      return Promise.resolve(this.selectedUsers);
    }
  }

  /**
   * Deletes a policy control by its ID.
   * If the policy control is not found, nothing is deleted.
   * 
   * @param policyControlId The ID of the policy control to delete.
   * @returns A promise that resolves when the policy control is deleted successfully, or rejects with an error if an error occurs.
   */
  async deletePolicyControl(policyControlId: string) {
    return this.adminService.deletePolicyControl(policyControlId)
      .catch((err) => {
        if (err.statusCode == 404) {
          // Policy control not found, nothing to delete
          return Promise.resolve();
        } else {
          return Promise.reject(err);
        }
      });
  }

  /**
   * Creates a policy control.
   * 
   * @param policyControlRequestBody - The request body for the policy control to create.
   * @returns A promise that resolves to the created policy control.
   */
  createPolicyControl(policyControlRequestBody: PolicyControlPostRequest) {
    return this.adminService.createPolicyControl(policyControlRequestBody);
  }

  async assignAccountsToSelectedUsers(accounts: AccountData[]) {
    let userOperationData: {
      userId: string,
      userName: string,
      policyControls: PolicyControlPostRequest[]
    }[] = [];

    this.selectedUsers.forEach(user => {
      let policyControlsToCreate = [];
      accounts.forEach((account) => {
        let existingPolicyControl = account.policyControls.find((policyControl) => policyControl.principalId == user.userId);
        if (!existingPolicyControl) {
          let policyControl: PolicyControlPostRequest = {
            accountId: this.accountsService.primeAccount,
            principalId: user.userId,
            targetAccountId: account.accountId,
            targetRole: 'ACCOUNT_ADMIN',
            name: account.name + ' ' + user.name,
          };
          policyControlsToCreate.push(policyControl);
        }
      });

      userOperationData.push({
        userId: user.userId,
        userName: user.displayName,
        policyControls: policyControlsToCreate
      });
    });

    return this.createProgressIndicatorAndPerformPolicyControlAction(
      policyControlAction.ASSIGN,
      userOperationData,
      accounts
    );
  }

  async unassignAccountsFromSelectedUsers(accounts: AccountData[]) {
    let userOperationData: {
      userId: string,
      userName: string,
      policyControls: PolicyControl[],
    }[] = [];

    this.selectedUsers.forEach(user => {
      let policyControlsToDelete = [];
      accounts.forEach((account) => {
        let policyControlForUser = account.policyControls.find((policyControl) => policyControl.principalId == user.userId);
        if (policyControlForUser) {
          policyControlsToDelete.push(policyControlForUser);
        }

      });
      userOperationData.push({
        userId: user.userId,
        userName: user.displayName,
        policyControls: policyControlsToDelete,
      });
    });

    return this.createProgressIndicatorAndPerformPolicyControlAction(
      policyControlAction.UNASSIGN,
      userOperationData,
      accounts
    );
  }

  async restrictUsers(userIds: string[]) {
    return this.setSelectedUsers(userIds).then(async (users) => {
      let pluralOrSingular = userIds.length > 1 ? 'plural' : 'singular';
      let translationKeyPrefix = 'accountManagement.progressIndicator.restrict';
      let progressData: ProgressIndicatorData = new ProgressIndicatorData();
      progressData.done = 0;
      progressData.total = users.length;
      progressData.updateSubject = new Subject();

      let modal = this.modalService.open(ProgressIndicatorPopupComponent, {
        backdrop: 'static',
        keyboard: false,
        centered: true,
      });
      modal.componentInstance.progressData = progressData;

      if (!users || users.length == 0) {
        let userString = this.translateService.instant('accountManagement.user');
        progressData.title = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.title`, { name: userString });
        progressData.successMessage = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.failure`, { name: userString });
        progressData.errorMessage = this.translateService.instant('accountManagement.errors.genericError');
        progressData.updateSubject.next(progressData);
      } else {
        progressData.actionString = this.translateService.instant(`${translationKeyPrefix}.action`);
        progressData.title = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.title`, { name: users[0].displayName });
        progressData.errorMessage = undefined;
        progressData.successMessage = undefined;
        
        let complete: number = 0;
        let failedResults: any[] = [];

        for (var i = 0; i < users.length; i++) {
          let user = users[i];
          let policyControl: PolicyControlPostRequest = {
            accountId: this.accountsService.primeAccount,
            principalId: user.userId,
            targetAccountId: this.accountsService.primeAccount,
            targetRole: UserRoles.ACCOUNT_ADMIN,
            name: 'Prime Account ' + user.name,
          };

          try {
            // to prevent the client from hammering the system
            await Util.timeout(10);
            await this.createPolicyControl(policyControl);
            complete++;
          } catch (err) {
            let errorInfo = {
              error: err,
              userId: user.userId,
              userName: user.displayName,
            }
            failedResults.push(errorInfo);
          }
          progressData.done++;
          progressData.updateSubject.next(progressData);
        }

        if (failedResults.length > 1) {
          progressData.successMessage = complete == 0 ? this.translateService.instant(`${translationKeyPrefix}.plural.failure`) : this.translateService.instant(`${translationKeyPrefix}.done`);
          progressData.errorMessage = this.translateService.instant(`${translationKeyPrefix}.plural.errorMessage`);
          failedResults.forEach(failedResult => {
            progressData.errorMessage = progressData.errorMessage + "<br/>" + failedResult.userName;
          });
        } else if (failedResults.length == 1) {
          progressData.successMessage = complete == 0 ? this.translateService.instant(`${translationKeyPrefix}.singular.failure`, { name: failedResults[0].userName }) : this.translateService.instant(`${translationKeyPrefix}.done`);
          progressData.errorMessage = this.translateService.instant(`${translationKeyPrefix}.singular.errorMessage`, { name: failedResults[0].userName });
        } else {
          progressData.successMessage = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.success`, { name: users[0].displayName });
        }
        progressData.updateSubject.next(progressData);
      }
      return modal.result.then(result => {
        modal = null;
        progressData.updateSubject = null;
        return;
      });
    });
  }

  async unrestrictUsers(userIds: string[]) {
    let pluralOrSingular = userIds.length > 1 ? 'plural' : 'singular';
    let translationKeyPrefix = 'accountManagement.progressIndicator.unrestrict';
    let progressData: ProgressIndicatorData = new ProgressIndicatorData();
    progressData.done = 0;
    progressData.total = userIds.length;
    progressData.updateSubject = new Subject();

    let modal = this.modalService.open(ProgressIndicatorPopupComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
    modal.componentInstance.progressData = progressData;

    return Promise.all([
      this.setSelectedUsers(userIds),
      this.getPolicyControls(),
    ]).then(async ([users, policyControls]) => {
      if (!users || users.length == 0) {
        let userString = this.translateService.instant('accountManagement.user');
        progressData.title = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.title`, { name: userString });
        progressData.successMessage = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.failure`, { name: userString });
        progressData.errorMessage = this.translateService.instant('accountManagement.errors.genericError');
        progressData.updateSubject.next(progressData);
      } else {
        progressData.actionString = this.translateService.instant(`${translationKeyPrefix}.action`);
        progressData.title = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.title`, { name: users[0].displayName });
        progressData.errorMessage = undefined;
        progressData.successMessage = undefined;

        let complete: number = 0;
        let failedResults: any[] = [];

        for (var i = 0; i < users.length; i++) {
          let user = users[i];
          let policyControlsForUser = policyControls.filter((policyControl) => policyControl.principalId == user.userId);
          let primeAccountPolicyControl = _.remove(policyControlsForUser, (policyControl) => this.isPrimeAccountId(policyControl.targetAccountId));
          let failedUnassignResults: any[] = [];

          for (var j = 0; j < policyControlsForUser.length; j++) {
            let policyControl = policyControlsForUser[j];
            try {
              // to prevent the client from hammering the system
              await Util.timeout(10);
              await this.deletePolicyControl(policyControl.policyControlId);
            } catch (err) {
              let errorInfo = {
                error: err,
                userId: user.userId,
                userName: user.displayName,
                accountId: policyControl.targetAccountId,
              }
              failedUnassignResults.push(errorInfo);
              // Should I inidicate that x account was not able to be unassigned so we could not unrestrict the user?
            }
          }

          if (failedUnassignResults.length > 0) {
            let errorInfo = {
              error: failedUnassignResults,
              userId: user.userId,
              userName: user.displayName
            }
            failedResults.push(errorInfo);
          } else { // only delete the prime account policy control if all others were deleted successfully
            try {
              // to prevent the client from hammering the system
              await Util.timeout(10);
              await this.deletePolicyControl(primeAccountPolicyControl[0].policyControlId);
              complete++;
            } catch (err) {
              let errorInfo = {
                error: err,
                userId: user.userId,
                userName: user.displayName
              }
              failedResults.push(errorInfo);
              // Should I inidicate that accounts that were all unassigned but the user was not unrestricted? How?
            }
          }

          progressData.done++;
          progressData.updateSubject.next(progressData);
        }

        if (failedResults.length > 1) {
          progressData.successMessage = complete == 0 ? this.translateService.instant(`${translationKeyPrefix}.plural.failure`) : this.translateService.instant(`${translationKeyPrefix}.done`);
          progressData.errorMessage = this.translateService.instant(`${translationKeyPrefix}.plural.errorMessage`);
          failedResults.forEach(failedResult => {
            progressData.errorMessage = progressData.errorMessage + "<br/>" + failedResult.userName;
          });
        } else if (failedResults.length == 1) {
          progressData.successMessage = complete == 0 ? this.translateService.instant(`${translationKeyPrefix}.singular.failure`, { name: failedResults[0].userName }) : this.translateService.instant(`${translationKeyPrefix}.done`);
          progressData.errorMessage = this.translateService.instant(`${translationKeyPrefix}.singular.errorMessage`, { name: failedResults[0].userName });
        } else {
          progressData.successMessage = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.success`, { name: users[0].displayName });
        }
        progressData.updateSubject.next(progressData);
      }


      return modal.result.then(result => {
        modal = null;
        progressData.updateSubject = null;
        return;
      });
    }).catch((err) => {
      let userString = this.translateService.instant('accountManagement.user');
      progressData.title = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.title`, { name: userString });
      progressData.successMessage = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.failure`, { name: userString });
      progressData.errorMessage = this.translateService.instant('accountManagement.errors.genericError');
      progressData.updateSubject.next(progressData);

      return modal.result.then(result => {
        modal = null;
        progressData.updateSubject = null;
        return;
      });
    });
  }

  async createProgressIndicatorAndPerformPolicyControlAction(
    action: policyControlAction,
    userOperationData: { userId: string, userName: string, policyControls: PolicyControl[] | PolicyControlPostRequest[] }[],
    accounts: AccountData[]
  ) {
    let pluralOrSingular = accounts.length > 1 ? 'plural' : 'singular';
    let translationKeyPrefix = `accountManagement.progressIndicator.${action}`;
    let progressData: ProgressIndicatorData = new ProgressIndicatorData();
    let errorMessage: string;
    progressData.done = 0;
    progressData.total = userOperationData.reduce((total, user) => total + user.policyControls.length, 0);
    progressData.title = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.titleName`, { name: userOperationData[0].userName });
    progressData.actionString = this.translateService.instant(`${translationKeyPrefix}.action`);
    progressData.errorMessage = undefined;
    progressData.successMessage = undefined;
    progressData.updateSubject = new Subject();

    let modal = this.modalService.open(ProgressIndicatorPopupComponent, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
    });
    modal.componentInstance.progressData = progressData;
    
    let complete: number = 0;
    for (var i = 0; i < userOperationData.length; i++) {
      let user = userOperationData[i];
      let failedResults: any[] = [];
      progressData.title = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.titleName`, { name: user.userName });
    
      for (var j = 0; j < user.policyControls.length; j++) {
        try {
          // to prevent the client from hammering the system
          await Util.timeout(10);
          if (action == policyControlAction.ASSIGN) {
            await this.createPolicyControl(user.policyControls[j]);
          } else if (action == policyControlAction.UNASSIGN) {
            await this.deletePolicyControl((user.policyControls[j] as PolicyControl).policyControlId);
          }
          complete++;
        } catch (err) {
          let errorInfo = {
            args: user.policyControls[j],
            error: err,
            userId: user.userId,
            userName: user.userName,
            accountId: user.policyControls[j].targetAccountId,
            accountName: accounts.find((account) => account.accountId == user.policyControls[j].targetAccountId)?.name,
          }
          failedResults.push(errorInfo);
        }
        progressData.done++;
        progressData.updateSubject.next(progressData);
      }

      if (failedResults.length > 0) {
        let msg = this.translateService.instant(`${translationKeyPrefix}.${failedResults.length > 1 ? 'plural' : 'singular'}.errorMessage`, { name: user.userName })
        errorMessage = errorMessage ? errorMessage + "<br/>" + msg : msg;
        failedResults.forEach(failedResult => {
          errorMessage = errorMessage + "<br/>" + failedResult.accountName;
        });
      }
    }
    
    progressData.errorMessage = errorMessage;
    if (userOperationData.length > 1) {
      progressData.title = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.title`);
    }
    if (complete == 0) {
      progressData.successMessage = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.failure`);
    } else if (complete == progressData.done) {
      progressData.successMessage = this.translateService.instant(`${translationKeyPrefix}.${pluralOrSingular}.success`);
    } else {
      progressData.successMessage = this.translateService.instant(`${translationKeyPrefix}.done`);
    }
    progressData.updateSubject.next(progressData);
  
    return modal.result.then(result => {
      modal = null;
      progressData.updateSubject = null;
      return;
    });
  }
}
